import {
  AfterViewInit,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Stage1viewmodeComponent } from '../project-brief/stage1viewmode/stage1viewmode.component';
import { Stage2viewmodeComponent } from '@/app/components/projects/project-stages/plan-sections/stage2viewmode/stage2viewmode.component';
import { ProjectPrintPreviewComponent } from './project-print-preview/project-print-preview.component';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TabbedScrollerService } from '@/app/services/tabbed-scroller.service';
import { GetProjectIdService } from '@/app/services/getProjectId.service';
import { ReloadPlanDataService } from '@/app/services/shared/reload-plan-data.service';
import { SendForApprovalComponent } from '@/app/components/global/dialog-boxes/send-for-approval/send-for-approval.component';
import { NgToastService } from 'ng-angular-popup';
import { ProjectsService } from '@/app/services/projects/projects.service';
import { DialogUtilityService } from '@/app/services/utility/dialog-utility.service';
import { ProjectWrapperComponent } from '../project-wrapper/project-wrapper.component';
interface ApprovalDetail {
  feedback: string | null;
  status: string;
  userName: string;
  userTitle: string | null;
  parentStatus: string;
}
@Component({
  standalone: true,
  imports: [
    Stage1viewmodeComponent,
    Stage2viewmodeComponent,
    ProjectPrintPreviewComponent,
    CommonModule,
    ProjectWrapperComponent,
  ],
  selector: 'app-brief-plan-view',
  templateUrl: './brief-plan-view.component.html',
  styleUrls: ['./brief-plan-view.component.css'],
})
export class BriefPlanViewComponent implements AfterViewInit, OnInit {
  @ViewChild('dialogTemplate') dialogTemplate!: TemplateRef<any>;
  projectBriefData: any;
  projectPlanResponsibilityData: any;
  projectPlanRiskRegisterData: any;
  isProjectOpen: boolean = false;
  isStageOneSendForApproval = false;
  progress = 0;
  approvalDetails: ApprovalDetail[] = [];

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private tabbedScrollerService: TabbedScrollerService,
    private getProjectIdService: GetProjectIdService,
    private reloadPlanDataService: ReloadPlanDataService,
    private route: ActivatedRoute,
    private toast: NgToastService,
    private projectService: ProjectsService,
    private dialogService: DialogUtilityService
  ) {}

  ngOnInit(): void {
    this.getStageOneApproversDetails();
    this.dialogService.closeProjectPlanDialog$.subscribe(() => {
      this.closeProjectDialog();
    });
    this.dialogService.closeProjectBriefDialog$.subscribe(() => {
      this.closeProjectDialog();
    });
    this.reloadPlanDataService.reloadPlanData$.subscribe((reload) => {
      if (reload) {
        this.checkStageOneApproval();
      }
    });
  }

  ngAfterViewInit() {
    this.route.queryParams.subscribe((params) => {
      const section = params['section'];
      if (section) {
        // Wait for data to load, then scroll
        const checkDataLoaded = setInterval(() => {
          if (this.projectBriefData) {
            clearInterval(checkDataLoaded);
            document
              .getElementById(section)
              ?.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100); // Check every 100ms
      }
    });
  }

  createPlan: boolean = false;

  openDialog(): void {
    this.dialog.open(this.dialogTemplate, {
      width: '80vw',
      height: 'auto',
      autoFocus: false,
    });
  }
  addProjectBriefData(data: any) {
    this.projectBriefData = data;

    // If there's a pending section scroll, execute it now
    setTimeout(() => {
      const section = this.route.snapshot.queryParams['section'];
      if (section) {
        document
          .getElementById(section)
          ?.scrollIntoView({ behavior: 'smooth' });
      }
    }, 0);
  }

  addProjectPlanData(data: any) {
    if (data.type === 'responsibilityMatrix') {
      this.projectPlanResponsibilityData = data.payload;
    } else if (data.type === 'riskRegister') {
      this.projectPlanRiskRegisterData = data.payload;
    }
  }
  handleClose() {
    this.router.navigate([`/projects`]);
  }
  handlePlanCreate() {
    this.createPlan = true;
    this.tabbedScrollerService.moveToPlanSection(true);
  }

  handlePlanEdit() {
    this.createPlan = true;
    this.tabbedScrollerService.moveToPlanSection(true);
  }

  handleDialog() {
    this.createPlan = false;
    this.isProjectOpen = false;
    this.tabbedScrollerService.moveToPlanSection(false);
    this.reloadPlanDataService.reloadPlanData();
  }

  handleEdit(projectId: string) {
    this.getProjectIdService.setProjectId(projectId);
    this.tabbedScrollerService.moveToPlanSection(false);
    this.isProjectOpen = true;
  }

  handleProjectStage1Approval() {
    const projectId = localStorage.getItem('projectId');
    if (!projectId) {
      this.toast.danger('Project ID not found', 'ERROR');
      return;
    }

    const dialogRef = this.dialog.open(SendForApprovalComponent, {
      width:'80vw',
      data: {
        progress: this.progress,
        stage: 1,
        projectId: projectId,
        approvalResponses: this.approvalDetails,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.toast.success('Approval sent successfully!', 'SUCCESS', 4000);
        this.isStageOneSendForApproval = true;
        this.checkStageOneApproval();
        localStorage.setItem('isApproved', 'true');
      }
    });
  }

  async checkStageOneApproval(): Promise<void> {
    /**
     * send request to check if approval for stage 1 exists.
     * show component sendforapproval/project-stage-approval based on the result
     */
    const projectId = localStorage.getItem('projectId');
    if (!projectId) {
      return;
    }

    const data = {
      project_id: projectId,
      stage: 1, // as this is info component so stage will always be 1
    };

    try {
      const temp = await this.projectService
        .getCurrentStageApproval(data)
        .toPromise();

      if (temp.data.length > 0) {
        this.isStageOneSendForApproval = true;
        this.approvalDetails = temp?.data?.map((item: any) => ({
          feedback: item.feedback,
          status: item.status,
          userName: item.user.name,
          userTitle: item.user.title,
          parentStatus: item?.parent_status,
          user: item?.user,
        }));
      }
    } catch (error) {
      console.log(error);
    }
  }

  getStageOneApproversDetails(): Promise<void> {
    const projectId = localStorage.getItem('projectId');
    if (!projectId) {
      return;
    }

    this.projectService
      .fetchCurrentStageApprovalPercentage(projectId, 1)
      .subscribe({
        next: (response) => {
          if (response.meta.success) {
            this.progress = response?.data;
          }
          this.checkStageOneApproval();
        },
        error: (error) => {
          console.error('Error while fetching project percentage:', error);
        },
      });
  }
  closeProjectDialog() {
    this.createPlan = false;
    this.isProjectOpen = false;
    this.reloadPlanDataService.reloadPlanData();
    this.dialog.closeAll();
  }
}
